import type React from 'react';
import { useState, useEffect } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { components } from 'react-select';
import { KText } from '@/ui-library/components/text';
import { StatusTag } from './RequisitionItem';
import { REQUISITION_TYPE_CONFIG } from '../../constants/enums';
import { KTag } from '@/ui-library/components/atoms/KTag';
import {
  PartOfGroupTag,
  GroupRequisitionCodes,
} from '@/components/requisitions/PartOfGroupTag';
import { TabBar } from '@/ui-library/components/atoms/KTabBar/KTabBar';

const renderRequisitionLocation = (locations?: string[]): string => {
  if (!locations || locations.length === 0) {
    return '';
  }

  if (locations.length === 1) {
    return locations[0];
  }

  if (locations.length === 2) {
    return locations.join(', ');
  }

  return `${locations[0]}, +${locations.length - 1}`;
};

export const RequisitionOption = (props: any) => {
  const { data, selectProps, innerProps } = props;
  const {
    status,
    department,
    locations,
    label,
    requisition_type,
    confidential,
    requisition_group_id,
    group_size,
    value,
    code,
    allRequisitions,
  } = data;

  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>('');

  // Reset selection when dialog visibility changes
  useEffect(() => {
    if (!showGroupDialog) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; not refactoring during a mechanical import move
      setSelectedOption('');
    }
  }, [showGroupDialog]);

  // Check if this requisition is part of a group
  const isPartOfGroup = !!requisition_group_id;

  // Get all group requisitions for this req
  const groupRequisitions = allRequisitions?.filter(
    (r: any) =>
      r.requisition_group_id === requisition_group_id &&
      // eslint-disable-next-line eqeqeq
      r.requisition_group_id != null
  );

  const tabOptions = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
  ];

  const handleTabChange = (id: string) => {
    setSelectedOption(id);
    if (id === 'yes') {
      // Select all group requisitions
      const groupReqs = allRequisitions?.filter(
        (r: any) =>
          r.requisition_group_id === requisition_group_id &&
          // eslint-disable-next-line eqeqeq
          r.requisition_group_id != null
      );

      // Transform to options
      const groupOptions =
        groupReqs?.map((req: any) => ({
          value: req.id,
          label: `${req.code} - ${req.role_name}`,
          status: req.status,
          department: req.ats_department?.name,
          locations: req.ats_offices?.map((office: any) => office.name) || [],
          requisition_type: req.requisition_type,
          confidential: req.confidential,
          requisition_group_id: req.requisition_group_id,
          group_size: req.group_size,
          code: req.code,
          ats_job: req.ats_job,
          requisition_association: req.requisition_association,
          allRequisitions,
        })) || [];

      if (selectProps.isMulti) {
        const currentValue = selectProps.value || [];
        const newValue = [...currentValue, ...groupOptions];
        // Remove duplicates
        const uniqueValue = Array.from(
          new Map(newValue.map((item: any) => [item.value, item])).values()
        );
        selectProps.onChange(uniqueValue, { action: 'select-option' });
      } else {
        selectProps.onChange(data, { action: 'select-option' });
      }
      setShowGroupDialog(false);
    } else if (id === 'no') {
      // Select only single requisition
      if (selectProps.isMulti) {
        const currentValue = selectProps.value || [];
        selectProps.onChange([...currentValue, data], {
          action: 'select-option',
        });
      } else {
        selectProps.onChange(data, { action: 'select-option' });
      }
      setShowGroupDialog(false);
    }
  };

  const handleOptionClick = (e: React.MouseEvent) => {
    // If part of a group, multi-select is enabled, dialog not shown yet, and group has more than 1 req, show dialog and prevent default
    if (
      isPartOfGroup &&
      selectProps.isMulti &&
      !showGroupDialog &&
      groupRequisitions.length > 1
    ) {
      e.stopPropagation();
      e.preventDefault();
      setShowGroupDialog(true);
      return;
    }

    // For non-grouped items, single-select, or when dialog is already shown, call original onClick
    if (innerProps?.onClick) {
      innerProps.onClick(e);
    }
  };

  const locationText = renderRequisitionLocation(locations);
  const hasDepartmentOrLocations = !!(department || locations?.length);

  const descriptionParts: string[] = [];
  if (department) {
    descriptionParts.push(department);
  }
  if (locationText) {
    descriptionParts.push(locationText);
  }
  if (requisition_type) {
    descriptionParts.push(REQUISITION_TYPE_CONFIG[requisition_type]?.label);
  }
  const description = descriptionParts.join(' · ');

  return (
    <components.Option
      {...props}
      innerProps={{
        ...innerProps,
        onClick: handleOptionClick,
      }}
    >
      <Box width="100%" overflow="hidden">
        <Flex
          alignItems="center"
          gap="6px"
          maxW="100%"
          overflow="hidden"
          justifyContent="start"
          mb={hasDepartmentOrLocations ? '4px' : '0'}
        >
          <KText
            color="secondary-900"
            size="sm-medium"
            maxW="70%"
            isTruncated
            enableTooltip={true}
          >
            {label}
          </KText>
          {status && <StatusTag status={status} />}
          {confidential && (
            <Tooltip
              label="The requisition associated with this job is confidential"
              placement="top"
              hasArrow
            >
              <Flex flexShrink={0}>
                <KTag value="Confidential requisition" width="max-content" />
              </Flex>
            </Tooltip>
          )}
          <PartOfGroupTag
            requisition={
              {
                id: value,
                requisition_group_id,
                group_size,
                code,
              } as any
            }
            allRequisitions={allRequisitions as any}
          />
        </Flex>
        {hasDepartmentOrLocations && (
          <KText maxW="100%" isTruncated display="inline">
            {description}
          </KText>
        )}

        {showGroupDialog && selectProps.isMulti && (
          <Box
            bg="white"
            p="12px"
            borderWidth="1px"
            borderColor="border-color"
            borderRadius="6px"
            mt="8px"
            onClick={(e) => e.stopPropagation()}
          >
            <Flex
              gap="6px"
              justifyContent="space-between"
              flexDirection="column"
            >
              <Flex
                gap="6px"
                alignItems="center"
                justifyContent="space-between"
              >
                <KText size="sm-medium" color="secondary-900">
                  This requisition is part of a group. Do you want to associate
                  all the related requisitions?
                </KText>
                <TabBar
                  options={tabOptions}
                  selectedId={selectedOption}
                  onChange={handleTabChange}
                />
              </Flex>
              <GroupRequisitionCodes
                requisitions={groupRequisitions}
                gap="6px"
              />
            </Flex>
          </Box>
        )}
      </Box>
    </components.Option>
  );
};
